/**
 * GraphQL type definitions matching backend schema — technology pillar.
 * Split from graphql/types.ts (issue #1409).
 */

import type { PageInfo } from './common';
import type { Service } from './service';

export enum TechnologyLifecycleStage {
  Adopt = 'Adopt',
  Hold = 'Hold',
  Evaluate = 'Evaluate',
  Sunset = 'Sunset',
}

export enum TechnologyLicenseType {
  OpenSource = 'open-source',
  Commercial = 'commercial',
  Enterprise = 'enterprise',
  Custom = 'custom',
}

export enum TechnologySecurityRating {
  High = 'high',
  Medium = 'medium',
  Low = 'low',
}

/** Ownership / origin flag — distinguishes Tyler-built/internal from vendor/open-source/partner (issue #564). */
export enum SupplierType {
  Internal = 'internal',
  Vendor = 'vendor',
  OpenSource = 'open_source',
  Partner = 'partner',
}

export interface Technology {
  entityId: string;
  entityType: string;
  name: string;
  description?: string | null;
  productFamilyId?: string | null;
  version?: string | null;
  lifecycleStage: TechnologyLifecycleStage;
  supplierId?: string | null;
  supplierType?: SupplierType | null;
  licenseType?: TechnologyLicenseType | null;
  supportEndDate?: string | null;
  securityRating?: TechnologySecurityRating | null;
  replacesId?: string | null;
  replacedById?: string | null;
  tags?: string[] | null;
  notes?: string | null;
  /** Published/draft/deprecated version records for this product (issue #1283). */
  versions?: TechnologyVersion[] | null;
  /** Count of Services using this Technology via USES_TECH edges; null on lookup failure (issue #1815). */
  usedByServiceCount?: number | null;
  _schemaVersion: string;
  _createdAt: string;
  _createdBy: string;
  _updatedAt: string;
  _updatedBy: string;
  _deletedAt?: string | null;
}

export interface TechnologyEdge {
  node: Technology;
  cursor: string;
}

/** Publish-state lifecycle for a TechnologyVersion record (issue #1283). */
export enum TechnologyVersionStatus {
  Draft = 'draft',
  Published = 'published',
  Deprecated = 'deprecated',
}

/**
 * A published/draft/deprecated version record for a Technology product
 * (issue #1283, part of #1282). Phase 1 minimal — see
 * documents/specs/technology-pillar-spec.md "TechnologyVersion Entity
 * (Phase 1 — Shipped)".
 */
export interface TechnologyVersion {
  entityId: string;
  entityType: string;
  technologyId: string;
  versionLabel: string;
  status: TechnologyVersionStatus;
  releaseDate?: string | null;
  notes?: string | null;
  _schemaVersion: string;
  _createdAt: string;
  _createdBy: string;
  _updatedAt: string;
  _updatedBy: string;
  _deletedAt?: string | null;
}

/**
 * USES_TECH edge metadata (issue #1284, part of #1282). `versionId`
 * optionally pins a specific published `TechnologyVersion` of the assigned
 * Technology product; `version` resolves that pin to the full entity.
 */
export interface UsesTechMetadata {
  notes?: string | null;
  role?: string | null;
  versionId?: string | null;
  version?: TechnologyVersion | null;
}

/**
 * A Service's assignment to a Technology product, with edge metadata
 * (issue #1284). Additive alongside `Service.technologies`, which stays a
 * flat `Technology[]` for existing consumers and cannot expose `notes`/a
 * pinned version.
 */
export interface ServiceTechnologyAssignment {
  serviceId: string;
  service?: Service | null;
  technologyId: string;
  technology?: Technology | null;
  metadata: UsesTechMetadata;
}

export interface TechnologyConnection {
  edges: TechnologyEdge[];
  pageInfo: PageInfo;
  totalCount?: number | null;
}

export interface TechnologyFilter {
  productFamilyId?: string;
  lifecycleStage?: TechnologyLifecycleStage;
  securityRating?: TechnologySecurityRating;
  supplierId?: string;
  supplierType?: SupplierType;
  includeDeleted?: boolean;
  /** Restrict to technologies used by these service IDs (via USES_TECH relationships, issue #491). */
  serviceIds?: string[];
  /** Free-text match against name/description/version, server-side (issue #765). */
  search?: string;
  /** Restrict to technologies tagged with any of these tag IDs, server-side (issue #765). */
  tagIds?: string[];
}
